import useCustomHook from '@/hooks/useCustomHook';
import { AudioAutoPlayerContext } from '@/shared/Players/AudioAutoPlayer/AudioAutoPlayerContext';
import { useContext, useEffect } from 'react';
import { ScreenStatus } from './sortImages.utils';
import { GameAssetsContext } from '@/shared/GameComponents/GameAssetsContext/GameAssetsContext';

export default function useQuestionAudio({ activityState, activityActions }) {
  const audioAutoPlayerContext = useContext(AudioAutoPlayerContext);
  const gameAssetsContext = useContext(GameAssetsContext);

  const { state, actions } = useCustomHook({
    defaultState: {
      screenIndex: null,
    },
    actions: {
      enqueueAudios: (currentScreen) => {
        if (currentScreen?.questions?.length) {
          const audioQueue = currentScreen.questions.map(
            (question) =>
              gameAssetsContext?.state?.sounds?.[question.id]?.audio ||
              question.audio
          );
          audioAutoPlayerContext.actions.createQueue(audioQueue);
          if (activityState.currentScreen?.status == ScreenStatus.audioPaused) {
            setTimeout(() => {
              audioAutoPlayerContext.actions.pause();
            }, 200);
          }
        }
      },
    },
  });

  useEffect(() => {
    audioAutoPlayerContext.actions.activate();

    return () => {
      audioAutoPlayerContext.actions.reset();
    };
  }, []);

  useEffect(() => {
    if (
      activityState.currentScreen?.index != state.screenIndex &&
      ScreenStatus.isAudio(activityState.currentScreen?.status)
    ) {
      actions.enqueueAudios(activityState.currentScreen);
      actions.setState({
        screenIndex: activityState.currentScreen?.index,
      });
    }
  }, [activityState.currentScreen?.index, activityState.currentScreen?.status]);

  useEffect(() => {
    const { currentAudio } = audioAutoPlayerContext.state;
    let audioIndexPlaying = null;
    if (currentAudio && activityState.currentScreen) {
      audioIndexPlaying = activityState.currentScreen.questions.findIndex(
        (question) =>
          question.audio == currentAudio ||
          gameAssetsContext?.state?.sounds?.[question.id]?.audio == currentAudio
      );
    }
    activityActions?.setAudioIndexPlaying?.(audioIndexPlaying);
  }, [
    audioAutoPlayerContext.state.currentAudio,
    audioAutoPlayerContext.state.playing,
  ]);

  useEffect(() => {
    if (activityState.currentScreen?.status == ScreenStatus.audioPlaying) {
      audioAutoPlayerContext.actions.resume();
    } else {
      setTimeout(() => {
        audioAutoPlayerContext.actions.pause();
      }, 1);
    }
  }, [activityState.currentScreen?.status]);

  useEffect(() => {
    if (
      ScreenStatus.audioPlaying == activityState.currentScreen?.status &&
      audioAutoPlayerContext.state.queueEnded
    ) {
      activityActions.setState({
        currentScreen: {
          ...activityState.currentScreen,
          status: ScreenStatus.activityPlaying,
        },
      });
    }
  }, [audioAutoPlayerContext.state.queueEnded]);

  useEffect(() => {
    if (
      ScreenStatus.audioPlaying == activityState.currentScreen?.status &&
      audioAutoPlayerContext.state.playingError &&
      !audioAutoPlayerContext.state.playing
    ) {
      activityActions.setState({
        currentScreen: {
          ...activityState.currentScreen,
          status: ScreenStatus.audioPaused,
        },
      });
    }
  }, [audioAutoPlayerContext.state.playingError]);

  return {
    state: { ...state, playing: audioAutoPlayerContext.state.playing },
    actions,
  };
}
